/**
 * Take a newly deployed shell without a shift-reload, and without eating
 * anybody's half-typed sentence.
 *
 * `vite-plugin-pwa`'s `injectRegister` only *registers* the worker. A tab that
 * stays open never fetches `sw.js`, and even once a new worker has taken over
 * the JavaScript already in memory is the old build. That is why a deploy
 * needed a hard refresh: the first ordinary load still came from the previous
 * precache, and nothing reloaded the document once the new worker had claimed
 * it.
 *
 * This is the rest of `registerType: 'autoUpdate'`, done with the browser's
 * own API rather than `virtual:pwa-register` — that virtual module pulls in
 * `workbox-window`, which is not a dependency and failed the production
 * build. Polling is what makes a long-lived tab notice the deploy at all:
 * Chrome will not check `sw.js` on its own for hours. Nothing here fires on a
 * first visit, where there is no controller yet: the browser activates that
 * worker by itself, and a page bounced on its own first load is a page that
 * flickers for no reason.
 *
 * ## The page decides when the new worker is let in
 *
 * A new worker installs and then **waits**, because `src/sw.ts` no longer calls
 * `self.skipWaiting()` of its own accord. This file is what releases it: at a
 * quiet moment it posts `{ type: 'SKIP_WAITING' }` to the waiting worker, and
 * then reloads the instant that worker claims the page. The wait and the
 * reload answer two different questions, and they answer them in opposite
 * directions.
 *
 * **Why the release waits.** A reload mid-gesture **eats un-sent work**. Not
 * the workspace — recipes and batches are commands in IndexedDB and an outbox,
 * and they survive anything. What does not survive is everything held in a
 * React state that has not been posted yet: a support message somebody has
 * spent five minutes writing, a feature request half typed into the board's
 * form, a note in a dialog. Those live in the document being replaced, and an
 * update that took the page the moment it landed threw them away silently, in
 * the middle of a sentence, for a deploy the person had no idea was happening.
 *
 * **Why the reload does not.** Activation is destructive. Workbox's precache
 * has one cache, and its `activate` handler deletes every entry the new
 * manifest does not name — so the old build's hashed chunks are gone the
 * instant the new worker takes over. A document still running the old bundle
 * that then navigates to a route it has not loaded yet `import()`s a chunk
 * nobody has any more: the cache misses, the request reaches the origin, the
 * file is not there either, and the SPA fallback in `wrangler.jsonc` answers
 * with `index.html` — a 200 of `text/html` where JavaScript was expected. That
 * is the phone error in PostHog, Safari's "'text/html' is not a valid
 * JavaScript MIME type" and Chrome's "Failed to fetch dynamically imported
 * module". Deferring the *activation* rather than the reload is what closes the
 * window: the old chunks stay cached for as long as the page still needs them,
 * and the moment it stops needing them, it reloads.
 *
 * So the release is scheduled rather than performed, by
 * {@link createQuietMomentScheduler}, at the first moment that is cheap:
 *
 * 1. **Now**, if the tab is already hidden. Nobody is typing into a tab they
 *    are not looking at.
 * 2. **When it next becomes hidden** — a switched tab, a locked phone. This is
 *    the common case, and it costs nothing at all.
 * 3. **After {@link IDLE_MS} of no keyboard or pointer activity**, for the
 *    monitor somebody has left the brew day open on and is not touching.
 * 4. **{@link CAP_MS} after the deploy regardless**, because the thing this
 *    whole file exists to minimise is a stale bundle talking to a live Worker
 *    (`vite.config.ts` states that), and a scheduler with no ceiling would let
 *    a tab that is genuinely in constant use drift for a whole working day.
 *
 * Ten minutes of skew is a bound worth having; a lost paragraph is not. The
 * policy itself is unchanged by any of the above — the same four moments, the
 * same numbers. What moved is which event it waits *from* (a worker waiting,
 * rather than a worker having already taken over) and what firing *does*
 * (release that worker, and reload when it claims the page).
 *
 * ## Two tabs, and the trade this makes
 *
 * A worker serves every tab on the origin, so whichever tab reaches its quiet
 * moment first releases it for all of them — and the others, which may be the
 * one somebody is typing into, reload immediately on `controllerchange`. That
 * is accepted rather than worked around. A lost paragraph in a second tab is a
 * worse outcome than the same paragraph lost in the first, but it is a far
 * better one than that tab navigating into a chunk the cache no longer holds
 * and showing an error page. On a phone, where this bug actually bites, a
 * PWA has one tab.
 *
 * Off in development. A service worker on the Vite module graph is a
 * debugging nightmare, and `pnpm dev` does not generate one
 * (`vite.config.ts`, `devOptions.enabled`).
 */

/** Cheap: `sw.js` is tiny and already served `must-revalidate`. */
const CHECK_EVERY_MS = 5 * 60 * 1000;

/**
 * How long the tab must go untouched before a reload counts as unobtrusive.
 *
 * A minute. Long enough that it never lands between two words — nobody pauses
 * that long inside a sentence they are still writing — and short enough to
 * catch the ordinary case of somebody who has stopped to read the screen or
 * gone to fetch the hydrometer.
 */
const IDLE_MS = 60 * 1000;

/**
 * The longest a tab may keep a new shell waiting behind it.
 *
 * Ten minutes. This is the number that keeps the quiet-moment policy honest:
 * the cost being managed is client/server skew, and a tab somebody is actively
 * working in is exactly the tab most likely to make a request the new Worker
 * answers differently. Ten minutes is longer than any single gesture and
 * shorter than a session.
 */
const CAP_MS = 10 * 60 * 1000;

/** What activity resets the idle clock. Listened for passively, on the window. */
const ACTIVITY_EVENTS = ['keydown', 'pointerdown', 'mousemove'] as const;

/** A scheduled reload, and the way to call it off. */
export interface QuietMomentScheduler {
  /** Cancel without firing. Nothing is left listening afterwards. */
  readonly cancel: () => void;
}

export interface QuietMomentOptions {
  /** What to do at the first quiet moment. Called at most once. */
  readonly onFire: () => void;
  /** How long the tab must be untouched. See {@link IDLE_MS}. */
  readonly idleMs: number;
  /** The ceiling, after which it fires regardless. See {@link CAP_MS}. */
  readonly capMs: number;
  /** Whether the tab is hidden *right now*. Defaults to reading `document`. */
  readonly isHidden?: () => boolean;
  /** Where `visibilitychange` is listened for. Defaults to `document`. */
  readonly visibilityTarget?: EventTarget;
  /** Where activity is listened for. Defaults to `window`. */
  readonly activityTarget?: EventTarget;
}

/**
 * Fire once, at the first moment that is cheap to interrupt.
 *
 * Pure scheduling and nothing else — it knows nothing about service workers or
 * about reloading, which is what makes it testable: the module it lives in only
 * runs in a production build, and this function runs anywhere with timers and
 * an event target. The three ways it can fire are the module header's, and the
 * order they are checked in is the order they cost the reader.
 *
 * **Everything is torn down on the first fire**, including the timer that did
 * not win, so `onFire` is called at most once however many of the conditions
 * become true at the same instant. {@link QuietMomentScheduler.cancel} is the
 * same teardown without the call.
 *
 * The hidden-now branch fires **synchronously**, before this function returns.
 * A caller that wants an asynchronous reload can have one; a caller that has
 * already decided to reload a tab nobody is looking at should not have to wait
 * for a tick to find out.
 */
export function createQuietMomentScheduler(options: QuietMomentOptions): QuietMomentScheduler {
  const {
    onFire,
    idleMs,
    capMs,
    isHidden = () => document.visibilityState === 'hidden',
    visibilityTarget = document,
    activityTarget = window,
  } = options;

  let done = false;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;
  let capTimer: ReturnType<typeof setTimeout> | undefined;

  const teardown = (): void => {
    done = true;
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    if (capTimer !== undefined) clearTimeout(capTimer);
    visibilityTarget.removeEventListener('visibilitychange', onVisibility);
    for (const event of ACTIVITY_EVENTS) {
      activityTarget.removeEventListener(event, onActivity);
    }
  };

  const fire = (): void => {
    if (done) return;
    teardown();
    onFire();
  };

  function onVisibility(): void {
    if (isHidden()) fire();
  }

  function onActivity(): void {
    if (done) return;
    // The idle clock restarts from the last thing the brewer did, never from
    // the deploy: a tab in continuous use never reaches it, which is what the
    // cap below is for.
    if (idleTimer !== undefined) clearTimeout(idleTimer);
    idleTimer = setTimeout(fire, idleMs);
  }

  // Already hidden: nobody is mid-anything, so there is nothing to wait for.
  if (isHidden()) {
    onFire();
    return { cancel: () => undefined };
  }

  visibilityTarget.addEventListener('visibilitychange', onVisibility);
  for (const event of ACTIVITY_EVENTS) {
    // Passive, because these fire on every mouse move and this listener has no
    // business being able to delay a scroll.
    activityTarget.addEventListener(event, onActivity, { passive: true });
  }
  idleTimer = setTimeout(fire, idleMs);
  capTimer = setTimeout(fire, capMs);

  return { cancel: teardown };
}

/** As much of a `ServiceWorker` as the flow below has any business knowing. */
export interface ShellWorker {
  readonly state: string;
  addEventListener(type: 'statechange', listener: () => void): void;
}

/** As much of a `ServiceWorkerRegistration`, likewise. */
export interface ShellRegistration<Worker extends ShellWorker> {
  readonly waiting: Worker | null;
  readonly installing: Worker | null;
  addEventListener(type: 'updatefound', listener: () => void): void;
}

export interface ShellUpdateFlowOptions<Worker extends ShellWorker> {
  /** The registration to watch. In production, the one `register()` resolved. */
  readonly registration: ShellRegistration<Worker>;
  /** Whether a worker is controlling this page *now*. A first visit has none. */
  readonly hasController: () => boolean;
  /** Tell a waiting worker to take over. In production, a `postMessage`. */
  readonly postSkipWaiting: (worker: Worker) => void;
  /** Replace the document with one built from the new shell. */
  readonly reload: () => void;
  /** Where `controllerchange` is heard. In production, `navigator.serviceWorker`. */
  readonly controllerChanges: EventTarget;
  /** How long the tab must be untouched. See {@link IDLE_MS}. */
  readonly idleMs?: number;
  /** The ceiling, after which it releases regardless. See {@link CAP_MS}. */
  readonly capMs?: number;
}

/**
 * Let a waiting worker in at a quiet moment, and reload once it has the page.
 *
 * The policy of the module header, with every collaborator injected, which is
 * what makes it testable: a registration and its workers are objects a browser
 * hands out and a test cannot construct, so this function asks only for the
 * three things it reads (`waiting`, `installing`, `updatefound`) and is handed
 * the two things it does (`postSkipWaiting`, `reload`).
 *
 * A worker can already be waiting when the page registers — somebody opened a
 * second tab after a deploy — or it can finish installing while the page is
 * open, which is `updatefound` followed by its worker reaching `installed`. It
 * can also be *mid-install* at the moment the flow starts, because `register()`
 * itself kicks off an update check and can have found the new script, fired
 * `updatefound` and left its worker installing before the promise it returned
 * resolves; that is the third case, and it is why the browser's own idiom reads
 * `installing || waiting || active` rather than just the last two.
 *
 * Either way the answer is one quiet moment, and only one however many builds
 * land during it: the release happens at the end, and the worker it releases is
 * whichever one is waiting *then*. A worker superseded during the wait has
 * already been discarded by the browser, and a message to it would go nowhere.
 *
 * The reload is not scheduled at all, because by the time the controller
 * changes the old build's chunks have been evicted and there is nothing left to
 * protect — see the header. It is guarded so two `controllerchange` events are
 * still one reload.
 */
export function createShellUpdateFlow<Worker extends ShellWorker>(
  options: ShellUpdateFlowOptions<Worker>,
): void {
  const {
    registration,
    hasController,
    postSkipWaiting,
    reload,
    controllerChanges,
    idleMs = IDLE_MS,
    capMs = CAP_MS,
  } = options;

  // A first visit has no controller. The browser activates that worker on its
  // own and `clientsClaim` fires `controllerchange` as it does; reloading then
  // would bounce somebody out of their first page load.
  const controlled = hasController();

  if (controlled) {
    let reloaded = false;
    controllerChanges.addEventListener('controllerchange', () => {
      if (reloaded) return;
      reloaded = true;
      reload();
    });
  }

  let scheduled = false;
  const releaseAtAQuietMoment = (): void => {
    if (!controlled || scheduled) return;
    scheduled = true;
    createQuietMomentScheduler({
      // Read at fire time, never captured: five minutes of polling can find a
      // newer build while this one waits, and the newer one is the one the
      // browser is actually holding.
      onFire: () => {
        const waiting = registration.waiting;
        if (waiting !== null) postSkipWaiting(waiting);
      },
      idleMs,
      capMs,
    });
  };

  /** Wait for a worker to finish installing, unless it already has. */
  const releaseWhenInstalled = (worker: Worker | null): void => {
    if (worker === null) return;
    if (worker.state === 'installed') {
      releaseAtAQuietMoment();
      return;
    }
    worker.addEventListener('statechange', () => {
      if (worker.state === 'installed') releaseAtAQuietMoment();
    });
  };

  // Both of the states a new worker can already be in by the time this runs.
  if (registration.waiting !== null) releaseAtAQuietMoment();
  releaseWhenInstalled(registration.installing);

  registration.addEventListener('updatefound', () => {
    releaseWhenInstalled(registration.installing);
  });
}

/**
 * The registration's update check, once there is a registration to ask.
 *
 * Armed by {@link startShellUpdates}; a no-op before that, and forever in
 * development and in a browser with no service worker, where there is no
 * shell to update and the "Reload needed" badge is the whole story.
 */
let checkNow: () => void = () => {};

/**
 * Look for a new build now rather than at the next five-minute poll.
 *
 * Called by the sync layer the moment it goes stale (`SyncClient`'s
 * `onStale`): a Worker that refuses this tab's protocol is proof that a newer
 * build is deployed, so the poll's wait is pure delay. The check is made even
 * in a hidden tab — the ordinary poll skips those because a hidden tab has
 * nothing to gain from polling, but a hidden *stale* tab is exactly the one
 * the quiet-moment scheduler would reload straight away, so finding the
 * build is what lets it. The reload itself still waits for a quiet moment,
 * for the reason the header gives.
 */
export function nudgeShellUpdate(): void {
  checkNow();
}

export function startShellUpdates(): void {
  if (!import.meta.env.PROD) return;
  if (!('serviceWorker' in navigator)) return;
  const container = navigator.serviceWorker;

  void container.register('/sw.js', { scope: '/' }).then((registration) => {
    createShellUpdateFlow({
      registration,
      hasController: () => container.controller !== null,
      postSkipWaiting: (worker) => worker.postMessage({ type: 'SKIP_WAITING' }),
      reload: () => window.location.reload(),
      controllerChanges: container,
    });

    const check = () => {
      if (document.visibilityState !== 'visible') return;
      void registration.update();
    };
    window.setInterval(check, CHECK_EVERY_MS);
    document.addEventListener('visibilitychange', check);
    checkNow = () => {
      void registration.update();
    };
  });
}
